import {
  Button,
  IconButton,
  InfoTooltip,
  Pill,
  SearchField,
} from '@wandb/components';
import React, {useCallback, useMemo, useState} from 'react';
import {Virtuoso} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {
  PanelQuickAddEvent,
  trackPanelQuickAdd,
} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {useStatsigGateMultiMetricQuickAdd} from '../../../util/rampFeatureFlags';
import {AddPanel} from '../../PanelBank/types';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddBulkButton} from './AddBulkButton';
import {BULK_ADD_LIMIT, PAGE_SIZE} from './constants';
import {FoundPanelsPill} from './FoundPanelsPill';
import {useQuickAddKeys} from './hooks/useQuickAddKeys';
import {KeyTypeIcon} from './KeyTypeIcon';
import {usePanelsQuickAddContext} from './PanelsQuickAddContext';
import {useVirtuosoComponents} from './virtuosoComponents';

type SingleKeyPanelAddProps = {
  searchStr: string;
  dSearchStr: string;
  setSearchStr: (value: string) => void;
  openMultiMetricForm: () => void;
};

export const SingleKeyPanelAdd = ({
  searchStr,
  dSearchStr,
  setSearchStr,
  openMultiMetricForm,
}: SingleKeyPanelAddProps) => {
  const isMultiMetricQuickAddEnabled = useStatsigGateMultiMetricQuickAdd();
  const {closeQuickAdd, onBack} = usePanelsQuickAddContext();
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const isGlobalQuickAdd = !targetSectionRef;

  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const trackQuickAddBound = useCallback(
    (event: Omit<PanelQuickAddEvent, 'scope'>) => {
      trackPanelQuickAdd({
        scope: isGlobalQuickAdd ? 'global' : 'section',
        isSearchActive: !!searchStr,
        ...event,
      });
    },
    [isGlobalQuickAdd, searchStr]
  );

  const {searchedKeys, numPanelsByKey, keysWithNoPanels} =
    useQuickAddKeys(dSearchStr);

  const [numKeysDisplayed, setNumKeysDisplayed] = useState(PAGE_SIZE);
  const displayedKeys = useMemo(
    () => searchedKeys.slice(0, numKeysDisplayed),
    [numKeysDisplayed, searchedKeys]
  );
  const showMoreKeys = useCallback(() => {
    // must check that there are actually more keys to display, otherwise
    // we'll be stuck in an infinite setState loop at the bottom of the list
    if (searchedKeys.length > displayedKeys.length) {
      setNumKeysDisplayed(prev => prev + PAGE_SIZE);
    }
  }, [displayedKeys.length, searchedKeys.length]);

  const dispatch = useDispatch();
  const onAddOne = useCallback(
    (key: string, spec: AddPanel['spec']) => {
      trackQuickAddBound({action: 'Add one clicked'});
      if (targetSectionRef) {
        dispatch(
          PanelBankSectionConfigActions.addPanelByConfig(
            targetSectionRef,
            getDefaultPanelConfig(key, spec),
            sortAlphabetically
          )
        );
      } else {
        dispatch(
          PanelBankConfigActions.addPanelsBySpec(
            panelBankConfigRef,
            [{key, spec}],
            sortAlphabetically
          )
        );
      }
    },
    [
      dispatch,
      panelBankConfigRef,
      sortAlphabetically,
      targetSectionRef,
      trackQuickAddBound,
    ]
  );

  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const numKeysAdded = numKeysInList - numKeysToAdd;
  const isLimitExceeded = numKeysToAdd > BULK_ADD_LIMIT;
  const isAllKeysAdded = numKeysInList > 0 && numKeysToAdd === 0;

  const isBulkAddAllowed =
    numKeysToAdd > 0 && !isLimitExceeded && !isAllKeysAdded;
  const isMultiMetricButtonShown =
    isMultiMetricQuickAddEnabled && numKeysInList > 1;
  const isFooterShown = isBulkAddAllowed || isMultiMetricButtonShown;

  const {components: virtuosoComponents, context: virtuosoContext} =
    useVirtuosoComponents({isFooterShown});

  return (
    <>
      <div className="border-b border-solid border-moon-250">
        {onBack != null && (
          <div className="flex items-center justify-between px-24 py-16">
            <IconButton
              variant="ghost"
              icon="back"
              aria-label="Back"
              onPress={onBack}
            />
            <IconButton
              variant="ghost"
              icon="close"
              aria-label="Close"
              onPress={closeQuickAdd}
            />
          </div>
        )}
        <div
          className={twMerge(
            'flex items-center justify-between px-24',
            onBack == null && 'py-16'
          )}>
          <h2 className="text-xl font-semibold leading-[32px]">
            Add panels from keys
          </h2>
          {onBack == null && (
            <IconButton
              variant="ghost"
              icon="close"
              aria-label="Close"
              onPress={closeQuickAdd}
            />
          )}
        </div>
        <p className="px-24 pb-16 text-moon-500">
          Easily generate panels from available keys. Don't see a specific key?
          Ensure that related runs are visualized in the workspace.
        </p>
        <div className="px-24 pb-16">
          <SearchField
            fullWidth
            aria-label="Search keys with regex"
            placeholder="Search keys with regex"
            value={searchStr}
            onChange={value => setSearchStr(value)}
            onBlur={() => trackQuickAddBound({action: 'Search box blurred'})}
          />
        </div>
      </div>
      <div className="flex h-[48px] items-center justify-between gap-16 border-b border-solid border-moon-250 bg-moon-50 px-24">
        <div className="flex items-center gap-8">
          <span className="text-sm font-semibold text-moon-500">
            {dSearchStr ? 'RESULTS' : 'KEYS'}
          </span>
          <div
            className="rounded bg-oblivion/[0.03] px-4 text-xs font-semibold text-moon-500"
            aria-describedby={
              isLimitExceeded ? 'bulk-add-limit-tooltip' : undefined
            }>
            {searchedKeys.length.toLocaleString()}
          </div>
          {isLimitExceeded && (
            <InfoTooltip
              id="bulk-add-limit-tooltip"
              aria-label="Bulk add limit tooltip"
              size="sm">
              You may add up to {BULK_ADD_LIMIT.toLocaleString()} keys at once.
              Refine your search to limit the results.
            </InfoTooltip>
          )}
        </div>
        {numKeysAdded > 0 && (
          <Pill
            icon="checkmark2"
            textValue={`${numKeysAdded.toLocaleString()} added`}
            color="green"
          />
        )}
      </div>
      <Virtuoso
        components={virtuosoComponents}
        context={virtuosoContext}
        data={displayedKeys}
        endReached={showMoreKeys}
        increaseViewportBy={PAGE_SIZE}
        itemContent={(idx, [key, spec]) => {
          const numPanelsGlobal = numPanelsByKey.global.get(key) ?? 0;
          const numPanelsInScope = numPanelsByKey.inScope.get(key) ?? 0;
          return (
            <div
              key={key}
              className={twMerge(
                'flex items-center justify-between gap-16 px-24 hover:bg-moon-100',
                isGlobalQuickAdd ? 'py-6' : 'py-10',
                '[&:hover_button[data-test="add-one-btn"]]:visible [&_button[data-test="add-one-btn"]]:invisible'
              )}>
              <div className="flex gap-8">
                <KeyTypeIcon
                  keyType={spec.keyType}
                  isApiAddedPanel={spec.type === 'api-added-panel'}
                />
                <div>
                  <p className="break-all">
                    {isGlobalQuickAdd &&
                    key.startsWith(spec.defaultSection + '/')
                      ? key.slice(spec.defaultSection.length + 1)
                      : key}
                  </p>
                  {isGlobalQuickAdd && (
                    <p className="break-all text-sm text-moon-500">
                      {spec.defaultSection}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-8">
                {!numPanelsGlobal && (
                  <Button
                    data-test="add-one-btn"
                    variant="secondary"
                    startIcon="add-new"
                    size="sm"
                    onPress={() => onAddOne(key, spec)}>
                    Add
                  </Button>
                )}
                {numPanelsGlobal > 0 && (
                  <FoundPanelsPill isInScope={numPanelsInScope > 0} />
                )}
              </div>
            </div>
          );
        }}
      />
      {isFooterShown && (
        <div className="border-t border-solid border-moon-250 p-16 flex items-center gap-12">
          {isBulkAddAllowed && (
            <div className="flex-1">
              <AddBulkButton
                searchedKeys={searchedKeys}
                keysWithNoPanels={keysWithNoPanels}
                trackQuickAddBound={trackQuickAddBound}
              />
            </div>
          )}
          {isMultiMetricButtonShown && (
            <div className="flex-1">
              <Button
                fullWidth
                variant="secondary"
                endIcon="chevron-next"
                onPress={openMultiMetricForm}>
                Create multi-metric panels
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
